/**
 * Bulletin (relevé de notes) d'un étudiant pour un semestre donné.
 * Reproduit la structure du PDF officiel : UE → ECUE avec D1, D2, Moy.D, SO, SR,
 * Moy.Ex, Crédits, Moy.ECUE + total UE + Moyenne générale + Mention + Décision.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader, Award, FileText, TrendingUp } from 'lucide-react';
import apiClient from '../../config/api.config';
import { formatNote } from '../../utils/formatNote';

interface Matiere {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  note_d1: number | null; absent_d1: boolean;
  note_d2: number | null; absent_d2: boolean;
  moyenne_devoirs: number | null;
  moyenne_devoirs_ponderee: number | null;
  note_so: number | null; absent_so: boolean;
  note_sr: number | null; absent_sr: boolean;
  moyenne_examen_finale: number | null;
  moyenne_examen_ponderee: number | null;
  moyenne_ecue: number | null;
  validee: boolean;
}
interface UE {
  id: number; code_ue: string; nom_ue: string;
  credits_total: number; credits_obtenus: number;
  moyenne_ue: number | null; validee: boolean;
  matieres: Matiere[];
}
interface Bulletin {
  etudiant: {
    id: number; matricule: string; nom: string; prenoms: string;
    date_naissance: string | null; lieu_naissance: string;
    classe: string; niveau: string; option: string; pole: string;
  };
  semestre: number; annee_academique: string;
  ues: UE[];
  moyenne_generale: number | null;
  credits_obtenus: number; credits_requis: number;
  mention: string;
  valide: boolean;
  decision: string;
  rang: number | null;
  effectif_classe: number | null;
}

interface Props {
  etudiantId: number;
  semestre: 1 | 2;
  anneeAcademique: string;
  onClose: () => void;
}

const BulletinModal: React.FC<Props> = ({ etudiantId, semestre, anneeAcademique, onClose }) => {
  const [data, setData] = useState<Bulletin | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const fetchBulletin = async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await apiClient.get('/resultats-semestre/bulletin/', {
          params: { etudiant_id: etudiantId, semestre, annee_academique: anneeAcademique },
        });
        setData(res.data);
      } catch (e: any) {
        setErr(e?.response?.data?.error || e?.message || 'Erreur chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchBulletin();
  }, [etudiantId, semestre, anneeAcademique]);

  return (
    <>
      {/* Styles d'impression : épure tout sauf le contenu utile */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #bulletin-print, #bulletin-print * { visibility: visible !important; }
          #bulletin-print {
            position: absolute !important; top: 0; left: 0;
            width: 100% !important; max-width: none !important;
            margin: 0 !important; padding: 12mm 10mm !important;
            box-shadow: none !important; border: none !important;
            border-radius: 0 !important; background: white !important;
            color: black !important;
          }
          #bulletin-print .no-print { display: none !important; }
          #bulletin-print table { border-collapse: collapse !important; }
          #bulletin-print th, #bulletin-print td {
            border: 1px solid #888 !important; color: black !important;
            font-size: 10pt !important; padding: 3pt 5pt !important;
          }
          #bulletin-print thead { background: #e5e7eb !important; }
          #bulletin-print h1, #bulletin-print h2, #bulletin-print h3 { color: black !important; }
          @page { size: A4 portrait; margin: 10mm; }
        }
      `}</style>
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto no-print">
      <div id="bulletin-print" className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between flex-shrink-0 no-print">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText size={20} className="text-blue-600" />
            Bulletin de notes — Semestre {semestre}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={22} />
          </button>
        </div>

        {/* En-tête imprimable (visible uniquement à l'impression) */}
        <div className="hidden print:block text-center pb-3 border-b-2 border-gray-700">
          <div className="text-sm font-semibold">MINISTÈRE DE L'ENSEIGNEMENT SUPERIEUR DE LA RECHERCHE SCIENTIFIQUE</div>
          <div className="text-base font-bold mt-1">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE 2I</div>
          <div className="text-lg font-bold mt-2">RELEVÉ DE NOTES — Semestre {semestre}</div>
          {data && <div className="text-xs mt-1">Année académique : {data.annee_academique}</div>}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader className="animate-spin mr-2" size={20} /> Calcul du bulletin…
            </div>
          )}
          {err && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded text-sm text-red-700">
              {err}
            </div>
          )}
          {!loading && !err && data && (
            <div className="space-y-4 print:space-y-2">
              {/* En-tête étudiant */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Matricule :</span> <strong>{data.etudiant.matricule}</strong></div>
                  <div><span className="text-gray-500">Année :</span> <strong>{data.annee_academique}</strong></div>
                  <div><span className="text-gray-500">Nom :</span> <strong>{data.etudiant.nom}</strong></div>
                  <div><span className="text-gray-500">Prénoms :</span> <strong>{data.etudiant.prenoms}</strong></div>
                  <div><span className="text-gray-500">Niveau / Classe :</span> <strong>{data.etudiant.niveau} / {data.etudiant.classe}</strong></div>
                  <div><span className="text-gray-500">Option :</span> <strong>{data.etudiant.option}</strong></div>
                </div>
              </div>

              {/* Tableau UE → ECUE */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold border border-gray-300 dark:border-gray-600">Modules / U.E</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-12">D.1</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-12">D.2</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-14">Moy.D<br/>(40%)</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-12">SO</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-12">SR</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-14">Moy.Ex<br/>(60%)</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-14">Crédits</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-16">Moy.ECUE</th>
                      <th className="px-1 py-2 text-center font-semibold border border-gray-300 dark:border-gray-600 w-20">Décision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ues.map(ue => (
                      <React.Fragment key={ue.id}>
                        {ue.matieres.map(m => (
                          <tr key={m.matiere_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100">
                              {m.nom_matiere}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center">
                              {m.absent_d1 ? <span className="text-orange-600">AB</span> : formatNote(m.note_d1)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center">
                              {m.absent_d2 ? <span className="text-orange-600">AB</span> : formatNote(m.note_d2)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center font-medium">
                              {formatNote(m.moyenne_devoirs_ponderee)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center">
                              {m.absent_so ? <span className="text-orange-600">AB</span> : formatNote(m.note_so)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center">
                              {m.note_sr !== null ? (m.absent_sr ? <span className="text-orange-600">AB</span> : formatNote(m.note_sr)) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center font-medium">
                              {formatNote(m.moyenne_examen_ponderee)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center">
                              {m.credits.toFixed(2)}
                            </td>
                            <td className={`px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center font-bold ${
                              m.moyenne_ecue !== null && m.moyenne_ecue >= 10 ? 'text-green-600' : 'text-gray-700 dark:text-gray-200'
                            }`}>
                              {formatNote(m.moyenne_ecue)}
                            </td>
                            <td className="px-1 py-1.5 border border-gray-200 dark:border-gray-700 text-center text-[10px]">
                              {m.validee ? <span className="text-green-600 font-semibold">V</span> : <span className="text-red-600">—</span>}
                            </td>
                          </tr>
                        ))}
                        {/* Ligne total UE — affiche les crédits TOTAUX de l'UE (somme des matières) */}
                        {/* Fix 2026-09-10 : afficher le nom_ue (contient déjà UE1, UE2…) */}
                        <tr className="bg-gray-200 dark:bg-gray-700 font-semibold">
                          <td className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                            {(() => {
                              const nom = (ue.nom_ue || '').trim();
                              if (/^UE\s?\d/i.test(nom)) return nom;
                              return nom || ue.code_ue;
                            })()}
                          </td>
                          <td colSpan={6} className="border border-gray-300 dark:border-gray-600"></td>
                          <td className="px-1 py-1.5 border border-gray-300 dark:border-gray-600 text-center">
                            {ue.credits_total.toFixed(2)}
                          </td>
                          <td className={`px-1 py-1.5 border border-gray-300 dark:border-gray-600 text-center ${
                            ue.moyenne_ue !== null && ue.moyenne_ue >= 10 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {formatNote(ue.moyenne_ue)}
                          </td>
                          <td className={`px-1 py-1.5 border border-gray-300 dark:border-gray-600 text-center text-[10px] ${
                            ue.validee ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {ue.validee ? 'Validé' : 'Non validé'}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                    {/* Ligne moyenne générale */}
                    <tr className="bg-yellow-50 dark:bg-yellow-900/20 font-bold border-t-2 border-yellow-400">
                      <td colSpan={8} className="px-2 py-2 border border-gray-300 dark:border-gray-600 text-right text-gray-800 dark:text-gray-100">
                        MOYENNE GÉNÉRALE
                      </td>
                      <td colSpan={2} className={`px-2 py-2 border border-gray-300 dark:border-gray-600 text-center text-lg ${
                        data.moyenne_generale !== null && data.moyenne_generale >= 10 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {formatNote(data.moyenne_generale)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Cartes synthèse */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded p-3 text-center">
                  <div className="text-xs text-blue-700 dark:text-blue-300 uppercase font-semibold">Crédits</div>
                  <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {data.credits_obtenus} / {data.credits_requis}
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 rounded p-3 text-center">
                  <div className="text-xs text-purple-700 dark:text-purple-300 uppercase font-semibold flex items-center justify-center gap-1">
                    <Award size={12} /> Mention
                  </div>
                  <div className="text-base font-bold text-purple-900 dark:text-purple-100">
                    {data.mention || '—'}
                  </div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded p-3 text-center">
                  <div className="text-xs text-amber-700 dark:text-amber-300 uppercase font-semibold flex items-center justify-center gap-1">
                    <TrendingUp size={12} /> Rang
                  </div>
                  <div className="text-base font-bold text-amber-900 dark:text-amber-100">
                    {data.rang ? `${data.rang}` : '—'}
                    {data.effectif_classe ? <span className="text-xs text-amber-700"> / {data.effectif_classe}</span> : ''}
                  </div>
                </div>
                <div className={`border rounded p-3 text-center ${
                  data.valide
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-300'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-300'
                }`}>
                  <div className={`text-xs uppercase font-semibold ${data.valide ? 'text-green-700' : 'text-red-700'}`}>
                    Décision
                  </div>
                  <div className={`text-sm font-bold ${data.valide ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                    {data.decision}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t dark:border-gray-700 flex justify-end gap-2 flex-shrink-0 no-print">
          <button onClick={onClose}
            className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300">
            Fermer
          </button>
          <button onClick={() => window.print()}
            className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
            Imprimer
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default BulletinModal;
